import type { AppError } from '@hitster/shared';
import { useT } from '../i18n.tsx';
import type { TranslationKey } from '../locales.ts';
import { IconClose } from './Icon.tsx';

/**
 * Kurzmeldung fuer Fehler, unten links aufgesetzt.
 *
 * Vorher war das ein `position: sticky`-Band am oberen Rand, direkt in
 * `App.tsx` verdrahtet. Zwei Gruende fuer den Umbau:
 *
 *  - Ein Band oben verschiebt bei jedem Fehler den Seiteninhalt. Mitten
 *    im Spiel wandert dadurch die Zeitleiste unter dem Finger weg.
 *  - Es lag in einem Flex-Container ohne wachsendes Textelement,
 *    weshalb der Schliessen-Knopf bei kurzen Meldungen mitten in der
 *    Zeile hing statt am rechten Rand.
 *
 * Unten links, weil unten rechts der Umschalter zur TV-Ansicht sitzt.
 *
 * `role="alert"` sorgt dafuer, dass Screenreader die Meldung vorlesen,
 * ohne dass der Fokus springt -- der Nutzer tippt oft noch.
 */
export function Toast({ error, onClose }: { error: AppError; onClose: () => void }) {
  const t = useT();

  return (
    <div className="toast" role="alert">
      <div className="toast__body">
        {t(`error.${error.code}` as TranslationKey)}
        {/*
         * Die Detailangabe kommt vom Server und ist nicht uebersetzt.
         * Sie steht trotzdem hier: bei Spotify-Fehlern ist sie oft der
         * einzige Hinweis auf die tatsaechliche Ursache.
         */}
        {error.detail && <span className="toast__detail">{error.detail}</span>}
      </div>

      {/* Ohne sichtbaren Knopf war nicht erkennbar, dass die Meldung
          wegklickbar ist. Sie blendet sich zusaetzlich selbst aus. */}
      <button
        type="button"
        className="toast__close"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <IconClose />
      </button>
    </div>
  );
}
